"use client";

import React, { useState, useRef, useEffect } from "react";
import { useDatePickerContext } from "./DatePickerContext";
import { Dayjs } from "dayjs";
import dayjs from "@dayjs";
import { MAX_STAY_DURATION_DAYS } from "@/constants/stay";
import DateInput from "./DateInput";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTheme } from "next-themes";
import { getWeekdays } from "@/app/utils/date/getWeekdays";
import { useLocale, useTranslations } from "next-intl";

const DesktopCalendars = () => {
  const { resolvedTheme } = useTheme();
  const { selectedDate, handleDateChange } = useDatePickerContext();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const t = useTranslations();
  const locale = useLocale();
  const calendarRef = useRef<HTMLDivElement | null>(null);
  const [currentMonth, setCurrentMonth] = useState(() =>
    selectedDate?.[0]
      ? dayjs(selectedDate[0]).startOf("month")
      : dayjs().startOf("month")
  );
  const isDarkMode = resolvedTheme === "dark";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        calendarRef.current &&
        !calendarRef.current.contains(event.target as Node)
      ) {
        if (selectedDate?.[0] && !selectedDate?.[1]) {
          const nextDay = dayjs(selectedDate[0]).add(1, "day");
          handleDateChange([selectedDate[0], nextDay]);
        }
        setIsCalendarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [selectedDate, handleDateChange]);

  const months = [currentMonth, currentMonth.add(1, "month")];

  const handleMonthChange = (direction: "prev" | "next") => {
    const earliestMonth = dayjs().startOf("month");
    const maxMonth = dayjs()
      .add(1, "year")
      .startOf("month")
      .subtract(1, "month");
    const newMonth =
      direction === "prev"
        ? currentMonth.subtract(1, "month")
        : currentMonth.add(1, "month");

    if (newMonth.isBefore(earliestMonth, "month")) {
      setCurrentMonth(earliestMonth);
    } else if (newMonth.isAfter(maxMonth, "month")) {
      setCurrentMonth(maxMonth);
    } else {
      setCurrentMonth(newMonth);
    }
  };

  const handleDayClick = (day: Dayjs) => {
    const newCheckIn = day.startOf("day");
    const currentCheckIn = selectedDate?.[0];
    const currentCheckOut = selectedDate?.[1];

    if (currentCheckIn && currentCheckOut) {
      handleDateChange([newCheckIn, null]);
    } else if (currentCheckIn && !currentCheckOut) {
      if (newCheckIn.isAfter(currentCheckIn)) {
        handleDateChange([currentCheckIn, newCheckIn]);
      } else {
        handleDateChange([newCheckIn, null]);
      }
    } else {
      handleDateChange([newCheckIn, null]);
    }
  };

  const getDaysInMonth = (month: Dayjs) => {
    const startOfMonth = month.startOf("month");
    const endOfMonth = month.endOf("month");
    const startOfWeek = startOfMonth.startOf("week");
    const endOfWeek = endOfMonth.endOf("week");

    const days = [];
    let day = startOfWeek;
    while (day.isBefore(endOfWeek) || day.isSame(endOfWeek, "day")) {
      days.push(day);
      day = day.add(1, "day");
    }
    return days;
  };

  const disabledDays = [
    { before: dayjs().startOf("day").toDate() },
    { after: dayjs().add(1, "year").toDate() },
    selectedDate?.[0] && !selectedDate?.[1]
      ? {
          after: dayjs(selectedDate[0])
            .add(MAX_STAY_DURATION_DAYS, "day")
            .toDate(),
        }
      : null,
  ].filter(
    (
      rule
    ): rule is
      | { before: Date; after?: undefined }
      | { after: Date; before?: undefined } =>
      rule !== null && rule !== undefined
  );

  const isDayDisabled = (day: Dayjs) => {
    return disabledDays.some((rule) => {
      if (rule.before && day.isBefore(rule.before, "day")) return true;
      if (rule.after && day.isAfter(rule.after, "day")) return true;
      return false;
    });
  };

  const isDaySelected = (day: Dayjs) => {
    const checkIn = selectedDate?.[0];
    const checkOut = selectedDate?.[1];
    const isToday = day.isSame(dayjs().startOf("day"), "day");

    if (!checkIn) {
      return {
        isSelected: false,
        isStart: false,
        isEnd: false,
        isMiddle: false,
        isToday,
      };
    }

    if (checkIn && !checkOut) {
      return {
        isSelected: day.isSame(checkIn, "day"),
        isStart: day.isSame(checkIn, "day"),
        isEnd: false,
        isMiddle: false,
        isToday,
      };
    }

    return {
      isSelected:
        day.isSame(checkIn, "day") ||
        day.isSame(checkOut, "day") ||
        (day.isAfter(checkIn, "day") && day.isBefore(checkOut, "day")),
      isStart: day.isSame(checkIn, "day"),
      isEnd: day.isSame(checkOut, "day"),
      isMiddle: day.isAfter(checkIn, "day") && day.isBefore(checkOut, "day"),
      isToday,
    };
  };

  const weekdays = getWeekdays(t);

  return (
    <div className="relative w-full font-inter">
      <DateInput onClick={() => setIsCalendarOpen((prev) => !prev)} />
      {isCalendarOpen && (
        <div
          className="calendar_modal-open absolute z-50 mt-4 rounded-[16px] sm:rounded-xl p-[2px] bg-sleap-gradiant"
          style={{ left: "50%", transform: "translateX(-50%)" }}
        >
          <div
            ref={calendarRef}
            className="bg-lightwhite dark:bg-sleap-gray-950 shadow-rooms-card rounded-[14px] sm:rounded-[10px] flex flex-col gap-4 p-0"
          >
            <div className="flex">
              {months.map((month, index) => (
                <div
                  key={index}
                  className={`p-4 ${index === 0 ? "border-r border-sleap-gray-200 dark:border-sleap-gray-600" : ""} flex flex-col gap-0.5 w-96`}
                >
                  <div className="relative flex items-center mb-6">
                    {index === 0 ? (
                      <>
                        <button
                          type="button"
                          onClick={() => handleMonthChange("prev")}
                          disabled={currentMonth.isSame(
                            dayjs().startOf("month"),
                            "month"
                          )}
                          className="p-2 rounded-full outline outline-1 outline-sleap-off-white-100 dark:outline-sleap-gray-700 outline-offset-[-1px] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronLeft
                            size={24}
                            className={
                              isDarkMode
                                ? "stroke-lightwhite"
                                : "stroke-sleap-gray-950"
                            }
                          />
                        </button>
                        <div className="flex-1" />
                      </>
                    ) : (
                      <div className="flex-1" />
                    )}
                    <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center gap-0.5">
                      <span className="text-sleap-gray-950 dark:text-lightwhite text-[14px] font-bold leading-[24px]">
                        {month.locale(locale.toLowerCase()).format("MMMM")}
                      </span>
                      <span className="text-sleap-gray-950 dark:text-lightwhite text-[14px] font-medium leading-[24px]">
                        {month.locale(locale.toLowerCase()).format("YYYY")}
                      </span>
                    </div>
                    {index === 1 ? (
                      <>
                        <div className="flex-1" />
                        <button
                          type="button"
                          onClick={() => handleMonthChange("next")}
                          disabled={currentMonth.isSame(
                            dayjs()
                              .add(1, "year")
                              .startOf("month")
                              .subtract(1, "month"),
                            "month"
                          )}
                          className="p-2 rounded-full outline outline-1 outline-sleap-off-white-100 dark:outline-sleap-gray-700 outline-offset-[-1px] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <ChevronRight
                            size={24}
                            className={
                              isDarkMode
                                ? "stroke-lightwhite"
                                : "stroke-sleap-gray-950"
                            }
                          />
                        </button>
                      </>
                    ) : (
                      <div className="flex-1" />
                    )}
                  </div>

                  <div className="flex gap-0.5 mb-0.5">
                    {weekdays.map((day) => (
                      <div
                        key={day}
                        className="w-[50px] h-[48px] flex items-center justify-center p-1 text-center text-sleap-gray-950 dark:text-lightwhite text-[14px] font-normal leading-[20px]"
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  <div className="grid grid-cols-7 gap-0.5 text-center">
                    {getDaysInMonth(month).map((day, dayIndex) => {
                      const isCurrentMonth = day.month() === month.month();
                      const isDisabled = isDayDisabled(day);
                      const { isSelected, isStart, isEnd, isMiddle, isToday } =
                        isDaySelected(day);

                      if (!isCurrentMonth) {
                        return <div key={dayIndex} className="p-3 opacity-0" />;
                      }

                      return (
                        <div
                          key={dayIndex}
                          onClick={() => !isDisabled && handleDayClick(day)}
                          className={`p-3 cursor-pointer text-[14px] font-normal leading-[20px] flex items-center justify-center min-w-[50px] max-w-[50px] min-h-[48px] max-h-[48px] relative box-border
                            ${isCurrentMonth ? "text-sleap-gray-950 dark:text-lightwhite" : "text-sleap-gray-400 dark:text-sleap-gray-600"}
                            ${isDisabled ? "cursor-not-allowed text-sleap-gray-400 dark:text-sleap-gray-600" : ""}
                            ${!isSelected && !isDisabled ? "hover:bg-sleap-gray-100 dark:hover:bg-sleap-gray-700 rounded-full" : ""}
                          `}
                        >
                          {isSelected && isStart && !isEnd && (
                            <div className="absolute h-12 top-0 bg-primary dark:bg-sleap-fern-100 rounded-full w-12 z-10" />
                          )}
                          {isSelected &&
                            (isStart || isMiddle || isEnd) &&
                            selectedDate?.[1] && (
                              <div
                                className={`absolute h-12 top-0 bg-sleap-sky-blue-200 dark:bg-sleap-fern-100 w-[51px] -ml-[0px] z-10
                                ${isStart ? "rounded-l-full" : ""}
                                ${isEnd ? "rounded-r-full w-[51px]" : ""}`}
                              />
                            )}
                          {isSelected &&
                            isStart &&
                            isEnd &&
                            day.isSame(selectedDate?.[0], "day") &&
                            day.isSame(selectedDate?.[1], "day") && (
                              <div className="absolute h-12 top-0 bg-primary dark:bg-sleap-fern-100 rounded-full w-12 z-10" />
                            )}

                          <div className="relative flex items-center justify-center w-full h-full">
                            {(isStart || isEnd) && (
                              <div className="absolute w-12 h-12 rounded-full bg-sky-blue dark:bg-primary z-20" />
                            )}
                            <span
                              className={`relative z-30 ${
                                isStart || isEnd
                                  ? "text-white dark:text-sleap-fern-100 font-semibold"
                                  : isToday && !isDisabled
                                    ? "text-sky-blue dark:text-primary font-normal"
                                    : isMiddle
                                      ? "text-sleap-gray-950 dark:text-lightwhite font-normal"
                                      : isDisabled
                                        ? "text-sleap-gray-400 dark:text-sleap-gray-600 font-normal"
                                        : "text-sleap-gray-950 dark:text-lightwhite font-normal"
                              }`}
                            >
                              {day.date()}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesktopCalendars;
